import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from '@emailjs/browser';
import { supabase } from '../../supabaseClient';
import contactData from '../../data/contact.json';
import contactPageData from '../../data/contact-page.json';
import { checkRateLimit, recordAttempt, formatRetryTime } from '../../utils/rateLimiter';
import { sanitizeFormData, isValidEmail } from '../../utils/sanitize';

const EMAILJS_SERVICE  = process.env.REACT_APP_EMAILJS_SERVICE;
const EMAILJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE;
const EMAILJS_AUTOREPLY= process.env.REACT_APP_EMAILJS_AUTOREPLY;
const EMAILJS_PUBLIC   = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const contactCards = contactData.cards;
const topics = contactData.topics;
const formSection = contactPageData.formSection;
const formContent = contactPageData.form;

export default function ContactMain() {
    const [form, setForm] = useState({ name: '', email: '', topic: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [honeypot, setHoneypot] = useState('');

    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);

    const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

    const handleSubmit = async e => {
        e.preventDefault();

        // Honeypot check — bots fill hidden fields
        if (honeypot) {
            setSubmitted(true); // Silently "succeed" to not alert the bot
            return;
        }

        // Rate limit check
        const rateCheck = checkRateLimit('contact');
        if (!rateCheck.allowed) {
            setError(`Too many submissions. Please try again in ${formatRetryTime(rateCheck.retryAfterMs)}.`);
            return;
        }

        if (!recaptchaToken) {
            setError('Please complete the CAPTCHA.');
            return;
        }

        // Record the attempt
        const attempt = recordAttempt('contact');
        if (!attempt.allowed) {
            setError(`Too many submissions. Please try again in ${formatRetryTime(attempt.retryAfterMs)}.`);
            return;
        }

        setSending(true);
        setError('');

        // Validate email format
        if (!isValidEmail(form.email)) {
            setSending(false);
            setError('Please enter a valid email address.');
            return;
        }

        // Sanitize form data before sending
        const cleanForm = sanitizeFormData(form);
        const templateParams = {
            from_name:  cleanForm.name,
            from_email: cleanForm.email,
            to_name:    cleanForm.name,
            to_email:   cleanForm.email,
            reply_to:   cleanForm.email,
            topic:      cleanForm.topic,
            message:    cleanForm.message,
        };
        try {
            // Save to Supabase
            const { error: dbError } = await supabase
                .from('enquiries')
                .insert([
                    { name: cleanForm.name, email: cleanForm.email, topic: cleanForm.topic, message: cleanForm.message }
                ]);
            
            if (dbError) {
                console.warn('Failed to save to Supabase:', dbError);
            }

            // Send notification to team
            await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, templateParams, EMAILJS_PUBLIC);
            // Send auto-reply to user — use separate params matching the reply template
            try {
                await emailjs.send(EMAILJS_SERVICE, EMAILJS_AUTOREPLY, {
                    to_name:    cleanForm.name,
                    from_email: cleanForm.email,
                    email:      cleanForm.email,
                    topic:      cleanForm.topic,
                    message:    cleanForm.message,
                }, EMAILJS_PUBLIC);
            } catch (replyErr) {
                console.warn('Auto-reply failed:', replyErr);
            }
            setSubmitted(true);
        } catch (err) {
            setError('Failed to send. Please email us directly at info@bsidescoimbatore.in');
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="cm-section">
            <style>{`
                .cm-section {
                    background: var(--eventflow-black);
                    padding: 80px 0 100px;
                    position: relative;
                    overflow: hidden;
                }
                .cm-section::before {
                    content: '';
                    position: absolute; inset: 0;
                    background: radial-gradient(ellipse at 30% 60%, rgba(var(--eventflow-primary-rgb),0.08) 0%, transparent 55%);
                    pointer-events: none;
                }

                /* contact cards */
                .cm-cards {
                    display: grid;
                    grid-template-columns: repeat(4,1fr);
                    gap: 16px;
                    margin-bottom: 64px;
                }
                .cm-card {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.07);
                    border-radius: 14px;
                    padding: 24px 20px;
                    text-decoration: none;
                    display: block;
                    transition: all 0.3s;
                    position: relative;
                    overflow: hidden;
                }
                .cm-card::before {
                    content: '';
                    position: absolute; top: 0; left: 0; right: 0; height: 2px;
                    background: linear-gradient(90deg, var(--eventflow-base), var(--eventflow-primary));
                    opacity: 0; transition: opacity 0.3s;
                }
                .cm-card:hover {
                    border-color: rgba(var(--eventflow-base-rgb),0.3);
                    transform: translateY(-4px);
                    box-shadow: 0 16px 40px rgba(0,0,0,0.3);
                }
                .cm-card:hover::before { opacity: 1; }
                .cm-card__icon {
                    width: 44px; height: 44px;
                    border-radius: 10px;
                    background: rgba(var(--eventflow-base-rgb),0.1);
                    display: flex; align-items: center; justify-content: center;
                    margin-bottom: 14px;
                    transition: background 0.3s;
                }
                .cm-card:hover .cm-card__icon { background: rgba(var(--eventflow-base-rgb),0.2); }
                .cm-card__icon i { font-size: 17px; color: var(--eventflow-base); }
                .cm-card__title {
                    font-family: var(--eventflow-font);
                    font-size: 13px; font-weight: 700;
                    color: rgba(255,255,255,0.5);
                    letter-spacing: 1px; text-transform: uppercase;
                    margin-bottom: 6px;
                }
                .cm-card__value {
                    font-family: var(--eventflow-font);
                    font-size: 14px; font-weight: 600;
                    color: #fff; margin-bottom: 6px;
                    word-break: break-word;
                }
                .cm-card__desc {
                    font-family: var(--eventflow-font);
                    font-size: 12px; color: rgba(255,255,255,0.35);
                    line-height: 1.5; margin: 0;
                }

                /* main layout */
                .cm-layout {
                    display: grid;
                    grid-template-columns: 1fr 480px;
                    gap: 56px;
                    align-items: start;
                    position: relative; z-index: 1;
                }

                /* left info */
                .cm-info__tag {
                    display: inline-flex; align-items: center; gap: 8px;
                    font-family: var(--eventflow-font);
                    font-size: 12px; font-weight: 700;
                    letter-spacing: 3px; text-transform: uppercase;
                    color: var(--eventflow-base); margin-bottom: 16px;
                }
                .cm-info__tag::before, .cm-info__tag::after {
                    content: ''; display: inline-block;
                    width: 28px; height: 1px;
                    background: var(--eventflow-base); opacity: 0.5;
                }
                .cm-info__title {
                    font-family: var(--eventflow-font);
                    font-size: 40px; font-weight: 900;
                    color: #fff; line-height: 1.1;
                    letter-spacing: -1px; margin-bottom: 16px;
                }
                .cm-info__title span { color: var(--eventflow-base); }
                .cm-info__desc {
                    font-family: var(--eventflow-font);
                    font-size: 15px; color: rgba(255,255,255,0.5);
                    line-height: 1.8; margin-bottom: 36px;
                }
                /* social links */
                .cm-socials {
                    display: flex; flex-wrap: wrap; gap: 10px;
                    margin-bottom: 40px;
                }
                .cm-social {
                    display: inline-flex; align-items: center; gap: 8px;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 50px; padding: 8px 18px;
                    font-family: var(--eventflow-font);
                    font-size: 13px; color: rgba(255,255,255,0.55);
                    text-decoration: none;
                    transition: all 0.25s;
                }
                .cm-social:hover {
                    border-color: rgba(var(--eventflow-base-rgb),0.4);
                    color: #fff; background: rgba(var(--eventflow-base-rgb),0.06);
                }
                .cm-social i { font-size: 14px; color: var(--eventflow-base); }
                /* response time */
                .cm-response {
                    display: flex; align-items: center; gap: 12px;
                    background: rgba(var(--eventflow-base-rgb),0.06);
                    border: 1px solid rgba(var(--eventflow-base-rgb),0.15);
                    border-radius: 12px; padding: 16px 20px;
                }
                .cm-response i { font-size: 20px; color: var(--eventflow-base); flex-shrink: 0; }
                .cm-response p {
                    font-family: var(--eventflow-font);
                    font-size: 13px; color: rgba(255,255,255,0.55);
                    margin: 0; line-height: 1.5;
                }
                .cm-response strong { color: #fff; }

                /* form card */
                .cm-form-card {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 16px; overflow: hidden;
                }
                .cm-form-card__header {
                    background: rgba(255,255,255,0.04);
                    border-bottom: 1px solid rgba(255,255,255,0.06);
                    padding: 20px 28px;
                }
                .cm-form-card__header h3 {
                    font-family: var(--eventflow-font);
                    font-size: 20px; font-weight: 800;
                    color: #fff; margin: 0 0 4px;
                }
                .cm-form-card__header p {
                    font-size: 13px; color: rgba(255,255,255,0.4); margin: 0;
                }
                .cm-form-card__body { padding: 28px; }
                .cm-field {
                    display: flex; flex-direction: column; gap: 6px;
                    margin-bottom: 14px;
                }
                .cm-field label {
                    font-family: var(--eventflow-font);
                    font-size: 11px; font-weight: 700;
                    letter-spacing: 1.5px; text-transform: uppercase;
                    color: rgba(255,255,255,0.4);
                }
                .cm-field input,
                .cm-field select,
                .cm-field textarea {
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 8px; padding: 11px 14px;
                    font-family: var(--eventflow-font);
                    font-size: 14px; color: #fff;
                    outline: none; width: 100%;
                    transition: border-color 0.3s;
                }
                .cm-field input::placeholder,
                .cm-field textarea::placeholder { color: rgba(255,255,255,0.25); }
                .cm-field input:focus,
                .cm-field select:focus,
                .cm-field textarea:focus { border-color: rgba(var(--eventflow-base-rgb),0.5); }
                .cm-field select option { background: #0d0010; color: #fff; }
                .cm-field textarea { resize: vertical; min-height: 130px; }
                .cm-field--error input,
                .cm-field--error select,
                .cm-field--error textarea { border-color: rgba(255,80,80,0.5); }
                .cm-field__err {
                    font-size: 11px; color: #ff6b6b;
                    font-family: var(--eventflow-font);
                }
                .cm-submit .thm-btn {
                    width: 100%; justify-content: center;
                    border-radius: 10px !important;
                    padding: 14px 24px !important;
                    font-size: 15px !important;
                }
                /* success */
                .cm-success {
                    text-align: center; padding: 48px 24px;
                }
                .cm-success__icon {
                    width: 72px; height: 72px; border-radius: 50%;
                    background: rgba(var(--eventflow-base-rgb),0.1);
                    border: 2px solid rgba(var(--eventflow-base-rgb),0.3);
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 20px;
                }
                .cm-success__icon i { font-size: 30px; color: var(--eventflow-base); }
                .cm-success h4 {
                    font-family: var(--eventflow-font);
                    font-size: 22px; font-weight: 800; color: #fff; margin-bottom: 10px;
                }
                .cm-success p {
                    font-size: 14px; color: rgba(255,255,255,0.45);
                    line-height: 1.7; margin: 0;
                }

                @media (max-width: 1199px) { .cm-layout { grid-template-columns: 1fr 420px; gap: 40px; } }
                @media (max-width: 991px)  {
                    .cm-layout { grid-template-columns: 1fr; }
                    .cm-cards { grid-template-columns: repeat(2,1fr); }
                }
                @media (max-width: 576px)  {
                    .cm-cards { grid-template-columns: 1fr; }
                    .cm-form-card__body { padding: 20px; }
                    .cm-info__title { font-size: 30px; }
                }
            `}</style>

            <div className="container">
                {/* Contact cards */}
                <div className="cm-cards wow fadeInUp" data-wow-delay="100ms">
                    {contactCards.map((c, i) => (
                        <a href={c.href} className="cm-card" key={i}
                            target={c.href.startsWith('http') ? '_blank' : undefined}
                            rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}>
                            <div className="cm-card__icon"><i className={`fa ${c.icon}`} /></div>
                            <div className="cm-card__title">{c.title}</div>
                            <div className="cm-card__value">{c.value}</div>
                            <p className="cm-card__desc">{c.desc}</p>
                        </a>
                    ))}
                </div>

                {/* Main layout */}
                <div className="cm-layout">
                    {/* Left */}
                    <div className="wow fadeInLeft" data-wow-delay="100ms">
                        <div className="cm-info__tag">{formSection.tag}</div>
                        <h2 className="cm-info__title">
                            {formSection.title}<span>{formSection.titleAccent}</span>
                        </h2>
                        <p className="cm-info__desc">
                            {formSection.description}
                        </p>

                        <div className="cm-socials">
                            {contactData.socials.map((s, i) => (
                                <a href={s.href} className="cm-social" key={i} target="_blank" rel="noopener noreferrer">
                                    <i className={s.icon} /> {s.label}
                                </a>
                            ))}
                        </div>

                        <div className="cm-response">
                            <i className="fa fa-clock" />
                            <p>{formSection.responseNote} <strong>{contactData.responseTime}</strong>. {formSection.responseUrgent}</p>
                        </div>
                    </div>

                    {/* Right form */}
                    <div className="wow fadeInRight" data-wow-delay="200ms">
                        <div className="cm-form-card">
                            <div className="cm-form-card__header">
                                <h3>{formContent.title}</h3>
                                <p>{formContent.subtitle}</p>
                            </div>
                            <div className="cm-form-card__body">
                                {submitted ? (
                                    <div className="cm-success">
                                        <div className="cm-success__icon"><i className="fa fa-check" /></div>
                                        <h4>{formContent.successTitle}</h4>
                                        <p>Thanks, <strong style={{color:'#fff'}}>{form.name}</strong>! We've received your message and will reply to <strong style={{color:'#fff'}}>{form.email}</strong> within 24 hours.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit}>
                                        {/* Honeypot field — hidden from real users, catches bots */}
                                        <div style={{position:'absolute',left:'-9999px',opacity:0,height:0,overflow:'hidden'}} aria-hidden="true">
                                            <input
                                                type="text"
                                                name="website_url"
                                                tabIndex={-1}
                                                autoComplete="off"
                                                value={honeypot}
                                                onChange={e => setHoneypot(e.target.value)}
                                            />
                                        </div>
                                        <div className="cm-field">
                                            <label>Your Name *</label>
                                            <input name="name" placeholder="John Doe" value={form.name} onChange={handleChange} required />
                                        </div>
                                        <div className="cm-field">
                                            <label>Email Address *</label>
                                            <input name="email" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange} required />
                                        </div>
                                        <div className="cm-field">
                                            <label>Topic *</label>
                                            <select name="topic" value={form.topic} onChange={handleChange} required>
                                                <option value="" disabled>Select a topic</option>
                                                {topics.map((t, i) => <option key={i}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div className="cm-field">
                                            <label>Message *</label>
                                            <textarea name="message" placeholder="Tell us how we can help..." value={form.message} onChange={handleChange} required />
                                        </div>
                                        <div className="cm-field" style={{ marginBottom: '10px' }}>
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                                onChange={val => setRecaptchaToken(val)}
                                                theme="dark"
                                            />
                                        </div>
                                        {error && <p style={{color:'#ff6b6b',fontSize:'13px',marginBottom:'12px'}}>{error}</p>}
                                        <div className="cm-submit">
                                            <button type="submit" className="thm-btn" disabled={sending}>
                                                {sending ? formContent.sendingText : formContent.submitText} <span className="icon-arrow-right" />
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


